import React, { useState, useImperativeHandle, forwardRef, useMemo, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Slider,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { Comments, FieldControl } from "../../../shared/components";
import { useDisplayField } from "../../../hooks/useDisplayField";
import usePhaseData from "../hooks/usePhaseData";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useActivityTodos } from "../hooks/useActivityTodos";
import { TodoItem } from "../../../shared/components";
import { qk } from "../../../lib";
import { getMe } from "../../auth";
import { useQuery } from "@tanstack/react-query";
import { clientTheme } from "../../../theme/clientTheme";

export interface ActivityFormHandle {
  submit: () => void;
}

interface ActivityFormProps {
  isEditing?: boolean;
  onSave: (data: ActivityFormData) => void;
  onCancel: () => void;
  initialData?: Partial<ActivityFormData>;
  phaseOptions?: Array<{ id: number; name: string }>;
  stageGateId: number | null | undefined;
  activityId: number | undefined;
  assetData: any[];
  projectId: string | undefined;
  phaseData?: any;
  activityDisabledConfig: Record<string, boolean>;
  isStageGateCompleted: boolean;
  isEstimateValidationStageGateCompleted: boolean;
  isContractorMode?: boolean;
}

export interface ActivityFormData {
  completedStatus: "completed" | "in_progress" | "not_started";
  isLoading: boolean;
  title: string;
  phase: string;
  category: string;
  category_key: string;
  category_display_text?: string | undefined;
  phaseId?: number;
  plannedStartDate: Dayjs | null;
  plannedEndDate: Dayjs | null;
  actualEndDate?: Dayjs | null;
  owner: string;
  userId?: number;
  progress: number;
  is_recurring: boolean;
  frequency: string;
  recurring_day: string[];
  description: string;
  asset_id: number | null;
  monthly_pattern: "day_of_month" | "day_of_week";
}

export const ActivityForm = forwardRef<ActivityFormHandle, ActivityFormProps>(
  function ActivityForm(
    {
      isEditing,
      onSave,
      initialData,
      phaseOptions = [],
      stageGateId,
      activityId,
      assetData,
      projectId,
      activityDisabledConfig,
      isStageGateCompleted,
      isEstimateValidationStageGateCompleted,
      isContractorMode = false,
    },
    ref
  ) {
    const weekdays = [
      { label: "M", value: "monday", key: "0" },
      { label: "T", value: "tuesday", key: "1" },
      { label: "W", value: "wednesday", key: "2" },
      { label: "T", value: "thursday", key: "3" },
      { label: "F", value: "friday", key: "4" },
      { label: "S", value: "saturday", key: "5" },
      { label: "S", value: "sunday", key: "6" },
    ];

    const defaultFormData: ActivityFormData = {
      completedStatus: "not_started",
      isLoading: false,
      title: "",
      phase: "",
      category: "",
      category_key: "",
      phaseId: undefined,
      plannedStartDate: null,
      plannedEndDate: null,
      actualEndDate: undefined,
      owner: "",
      userId: undefined,
      progress: 0,
      is_recurring: false,
      frequency: "",
      recurring_day: [],
      description: "",
      asset_id: null,
      monthly_pattern: "day_of_week",
    };

    const fileUploadRef = useRef<any>(null);

    const { getFilterOptions } = useDisplayField([]);
    const [currentTab, setCurrentTab] = useState(0);

    const { data } = useQuery({
      queryKey: qk.me(),
      queryFn: getMe,
    });

    const user = data?.user?.id;

    const { isAllowedToAddActivityTodo, isAllowedToCompleteActivityTodo } = useCapabilities();

    const { data: stakeholders, isLoading: isLoadingUsers } = useGetJeopardyStakeholders({
      params: { project_id: projectId },
    });

    const userOptions = useMemo(() => {
      return (
        stakeholders?.data?.flatMap((role: any) =>
          role?.users?.map((user: any) => ({
            role: role.role_name,
            name: user.user_name,
            phone: user.phone || "N/A",
            email: user.email,
            id: user.user_id,
          }))
        ) || []
      );
    }, [stakeholders, isLoadingUsers]);

    const [formData, setFormData] = useState<ActivityFormData>({
      ...defaultFormData,
      ...initialData,
    });

    const { parentPhase } = usePhaseData({
      projectId: Number(projectId),
      phaseId: Number(formData?.phaseId),
    });

    const phaseStartDate = useMemo(
      () => (parentPhase?.planned_start_date ? dayjs(parentPhase.planned_start_date).startOf("day") : null),
      [parentPhase]
    );
    const phaseEndDate = useMemo(
      () => (parentPhase?.planned_end_date ? dayjs(parentPhase.planned_end_date).startOf("day") : null),
      [parentPhase]
    );

    const [errors, setErrors] = useState<{
      title?: string;
      phaseId?: string;
      category_key?: string;
      plannedStartDate?: string;
      plannedEndDate?: string;
    }>({});

    const [warningQueue, setWarningQueue] = useState<string[]>([]);

    const currentWarning = warningQueue[0] ?? null;

    const handleWarningIgnore = () => {
      const remaining = warningQueue.slice(1);
      setWarningQueue(remaining);
      if (remaining.length === 0) {
        onSave(formData);
      }
    };

    const handleWarningCancel = () => {
      setWarningQueue([]);
    };

    const getMonthlyDescription = () => {
      if (!formData.plannedStartDate) return "";
      const date = dayjs(formData.plannedStartDate);
      const dayOfMonth = date.date();
      const dayName = date.format("dddd");
      const weekOfMonth = Math.ceil(dayOfMonth / 7);
      const ordinal = ["first", "second", "third", "fourth", "fifth"][weekOfMonth - 1] || weekOfMonth;
      return formData.monthly_pattern === "day_of_month"
        ? `On day ${dayOfMonth} of every month`
        : `On the ${ordinal} ${dayName} of every month`;
    };

    const toggleWeekday = (dayKey: string) => {
      const currentWeekdays = formData.recurring_day || [];
      const newWeekdays = currentWeekdays.includes(dayKey)
        ? currentWeekdays.filter((d: string) => d !== dayKey)
        : [dayKey?.toString()];
      setFormData({ ...formData, recurring_day: newWeekdays });
    };

    const {
      todos,
      createTodo,
      updateTodo,
      isCreating,
      message,
      setMessage
    } = useActivityTodos({ projectId: String(projectId), activityId: Number(activityId) });

    const activityOwnerId = initialData?.userId ?? formData.userId;

    const canAddTodo = isAllowedToAddActivityTodo || user === activityOwnerId;
    const canCompleteTodo = isAllowedToCompleteActivityTodo || user === activityOwnerId;

    const [todoDescription, setTodoDescription] = useState("");
    const [showAddTodo, setShowAddTodo] = useState(false);
    const [updatingTodoId, setUpdatingTodoId] = useState<number | null>(null);

    const handleAddTodo = async () => {
      if(!todoDescription.trim()) return;

      await createTodo({ description: todoDescription.trim()});

      setTodoDescription("");
    };


    useEffect(() => {
      if (isEditing && initialData) {
        setFormData((prev) => ({
          ...prev,
          title: initialData.title ?? prev.title,
          phase: initialData.phase ?? prev.phase,
          phaseId: initialData.phaseId ?? prev.phaseId,
          category: initialData?.category ?? prev.category,
          category_key: initialData?.category_key ?? prev.category_key,
          plannedStartDate: initialData.plannedStartDate ?? prev.plannedStartDate,
          plannedEndDate: initialData.plannedEndDate ?? prev.plannedEndDate,
          actualEndDate: initialData.actualEndDate ?? prev.actualEndDate,
          owner: initialData.owner ?? prev.owner,
          userId: initialData.userId ?? prev.userId,
          progress: initialData.progress ?? prev.progress,
          asset_id: initialData.asset_id ?? prev.asset_id,
          is_recurring: initialData.is_recurring ?? prev.is_recurring,
          frequency: initialData.frequency ?? prev.frequency,
          recurring_day:
            (initialData.recurring_day && initialData.recurring_day.map((r) => r && r.toString())) ||
            prev.recurring_day,
          monthly_pattern: initialData.monthly_pattern ?? prev.monthly_pattern,
        }));
      } else {
        setFormData(defaultFormData);
      }
    }, [initialData]);

    const endDateMinDate = useMemo(() => {
      const min = formData.plannedStartDate
        ? dayjs(formData.plannedStartDate).startOf("day")
        : isStageGateCompleted
        ? dayjs().startOf("day")
        : undefined;
      return min;
    }, [formData.plannedStartDate, isStageGateCompleted]);

    const shouldDisablePlannedEndDate = (date: Dayjs) => {
      const today = dayjs().startOf("day");

      if (formData.plannedStartDate && date.isBefore(dayjs(formData.plannedStartDate).startOf("day"))) {
        return true;
      }

      if (!isStageGateCompleted) return false;

      if (formData.plannedEndDate && date.isSame(dayjs(formData.plannedEndDate), "day")) {
        return false;
      }

      if (initialData?.plannedEndDate && date.isSame(dayjs(initialData.plannedEndDate), "day")) {
        return false;
      }

      return date.isBefore(today);
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        const newErrors: {
          title?: string;
          phaseId?: string;
          category_key?: string;
          plannedStartDate?: string;
          plannedEndDate?: string;
        } = {};
        if (!formData.title) newErrors.title = "Activity Title is required";
        if (!formData.phaseId) newErrors.phaseId = "Phase is required";
        if (!formData.category_key) newErrors.category_key = "Category is required";
        if (!formData.plannedStartDate) newErrors.plannedStartDate = "Planned Start Date is required";
        if (!formData.plannedEndDate) newErrors.plannedEndDate = "Planned End Date is required";
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          return;
        }

        const warnings: string[] = [];

        if (formData.plannedStartDate && formData.plannedEndDate) {
          const isStartBeforePhaseStart =
            phaseStartDate && formData.plannedStartDate.isBefore(phaseStartDate, "day");
          const isStartAfterPhaseEnd = phaseEndDate && formData.plannedStartDate.isAfter(phaseEndDate, "day");
          const isEndBeforePhaseStart = phaseStartDate && formData.plannedEndDate.isBefore(phaseStartDate, "day");
          const isEndAfterPhaseEnd = phaseEndDate && formData.plannedEndDate.isAfter(phaseEndDate, "day");

          if (isStartBeforePhaseStart || isStartAfterPhaseEnd || isEndBeforePhaseStart || isEndAfterPhaseEnd) {
            warnings.push(
              "The selected planned dates fall outside the planned dates of the parent phase. Are you sure you want to proceed?"
            );
          }
        }

        if (formData.actualEndDate) {
          const isActualBeforePlannedStart =
            formData.plannedStartDate && formData.actualEndDate.isBefore(formData.plannedStartDate, "day");
          if (isActualBeforePlannedStart) {
            warnings.push(
              "The actual end date is earlier than the planned start date. Are you sure you want to proceed?"
            );
          }
        }

        if (warnings.length > 0) {
          setWarningQueue(warnings);
        } else {
          onSave(formData);
        }
      },
    }));

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="OVERVIEW" />
            <Tab label="TODO" disabled={!stageGateId || !activityId} />
            <Tab label="COMMENTS" disabled={!stageGateId || !activityId} />
          </Tabs>

          {initialData && isStageGateCompleted && (
            <Box px={2} mb={2}>
              <Alert severity="info">Cautious: The linked stage gate has been approved.</Alert>
            </Box>
          )}

          {currentTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 2, pb: 2 }}>
              <TextField
                fullWidth
                label="Activity Title"
                required
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  setErrors({ ...errors, title: undefined });
                }}
                disabled={activityDisabledConfig.title}
                error={!!errors.title}
                helperText={errors.title}
              />

              <FormControl fullWidth required error={!!errors.phaseId}>
                <InputLabel>Phase</InputLabel>
                <Select
                  value={formData.phaseId || ""}
                  label="Phase"
                  onChange={(e) => {
                    const newlySelectedPhaseId = e.target.value as number;
                    const selectedPhase = phaseOptions.find((p) => p.id === newlySelectedPhaseId);
                    setFormData({
                      ...formData,
                      phaseId: newlySelectedPhaseId,
                      phase: selectedPhase?.name || "",
                      plannedStartDate: null,
                      plannedEndDate: null,
                    });
                    setErrors({ ...errors, phaseId: undefined });
                  }}
                  disabled={activityDisabledConfig.phase}
                >
                  {phaseOptions.length > 0 ? (
                    phaseOptions.map((phase) => (
                      <MenuItem key={phase.id} value={phase.id}>
                        {phase.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No phases available
                    </MenuItem>
                  )}
                </Select>
                {errors.phaseId && <FormHelperText>{errors.phaseId}</FormHelperText>}
              </FormControl>

              <Box>
                <FieldControl
                  keyName={"category_id"}
                  label={"Category"}
                  variant={"select"}
                  value={formData.category_key || ""}
                  options={
                    isEditing && initialData?.category_key === "Commercial_Focus"
                      ? getFilterOptions("ACTIVITY_CATEGORY").map((value) => ({
                          label: value.display_text,
                          value: value.key,
                        }))
                      : isEstimateValidationStageGateCompleted
                      ? getFilterOptions("ACTIVITY_CATEGORY")
                          .filter((option) => option.key !== "Commercial_Focus")
                          .map((value) => ({ label: value.display_text, value: value.key }))
                      : getFilterOptions("ACTIVITY_CATEGORY").map((value) => ({
                          label: value.display_text,
                          value: value.key,
                        }))
                  }
                  onChange={(_key: string, value: any) => {
                    setFormData({ ...formData, category_key: value });
                    setErrors({ ...errors, category_key: undefined });
                  }}
                  disabled={
                    isContractorMode
                      ? true
                      : isEditing
                      ? isEstimateValidationStageGateCompleted && formData.category_key === "Commercial_Focus"
                      : activityDisabledConfig.category
                  }
                  error={!!errors.category_key}
                  helperText={errors.category_key}
                  required
                />
              </Box>

              {!clientTheme.layout?.hideProjectAssetDetails && (
              <FieldControl
                keyName={"asset_id"}
                label={"Assets"}
                variant={"select"}
                value={formData.asset_id}
                options={assetData.map((value) => ({ label: value.asset_description, value: value.asset_id }))}
                onChange={(_key: string, value: any) => {
                  setFormData({ ...formData, asset_id: value });
                }}
                disabled={activityDisabledConfig.asset_id}
              />
              )}

              <DatePicker
                label="Planned Start Date"
                value={formData.plannedStartDate}
                onChange={(date) => {
                  let newPlannedEndDate = formData.plannedEndDate;
                  if (date && newPlannedEndDate && date.isAfter(newPlannedEndDate.startOf("day"))) {
                    newPlannedEndDate = date;
                  }
                  setFormData({ ...formData, plannedStartDate: date, plannedEndDate: newPlannedEndDate });
                  setErrors({ ...errors, plannedStartDate: undefined });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.plannedStartDate,
                    helperText: errors.plannedStartDate,
                  },
                }}
                format="DD/MM/YYYY"
                disabled={activityDisabledConfig.plannedStartDate}
              />

              <DatePicker
                label="Planned End Date"
                value={formData.plannedEndDate}
                onChange={(date) => {
                  date && setFormData({ ...formData, plannedEndDate: date });
                  setErrors({ ...errors, plannedEndDate: undefined });
                }}
                minDate={endDateMinDate}
                shouldDisableDate={shouldDisablePlannedEndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.plannedEndDate,
                    helperText: errors.plannedEndDate,
                  },
                }}
                format="DD/MM/YYYY"
                disabled={activityDisabledConfig.plannedEndDate}
              />

              <DatePicker
                label="Actual End Date"
                value={formData.actualEndDate || null}
                onChange={(date) => {
                  setFormData({ ...formData, actualEndDate: date || undefined });
                }}
                slotProps={{ textField: { fullWidth: true } }}
                maxDate={dayjs().startOf("day")}
                format="DD/MM/YYYY"
                disabled={activityDisabledConfig.actualEndDate || formData.progress !== 100}
              />

              <FormControl fullWidth>
                <InputLabel>Activity Owner</InputLabel>
                <Select
                  value={formData.userId || ""}
                  label="Activity Owner"
                  onChange={(e) => {
                    const selectedUser = userOptions.find((u) => u.id === e.target.value);
                    setFormData({ ...formData, userId: e.target.value as number, owner: selectedUser?.name || "" });
                  }}
                  disabled={isLoadingUsers || activityDisabledConfig.owner}
                >
                  {isLoadingUsers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : userOptions.length > 0 ? (
                    userOptions.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No users available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <Box sx={{ px: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Percentage Completed
                </Typography>
                <Slider
                  value={formData.progress}
                  onChange={(_, value) => {
                    const newProgress = value as number;
                    setFormData({
                      ...formData,
                      progress: newProgress,
                      actualEndDate: newProgress === 100 ? dayjs().startOf("day") : undefined,
                    });
                  }}
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={0}
                  max={100}
                  sx={{ mt: 2 }}
                  disabled={activityDisabledConfig.progress}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="caption">0</Typography>
                  <Typography variant="caption">25</Typography>
                  <Typography variant="caption">50</Typography>
                  <Typography variant="caption">70</Typography>
                  <Typography variant="caption">100%</Typography>
                </Box>
              </Box>

              <FormControlLabel
                sx={{ px: 1 }}
                key={formData.is_recurring ? "recurring-true" : "recurring-false"}
                control={
                  <Checkbox
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  />
                }
                label={"Is Recurring"}
                disabled={activityDisabledConfig.is_recurring}
              />

              {formData.is_recurring && (
                <FieldControl
                  keyName={""}
                  label={"Frequency"}
                  variant={"select"}
                  value={formData.frequency}
                  options={getFilterOptions("FREQUENCY").map((opt) => ({ value: opt.key, label: opt.display_text }))}
                  onChange={(_key: string, value: any) => {
                    setFormData({ ...formData, frequency: value });
                  }}
                  disabled={activityDisabledConfig.frequency}
                />
              )}

              {formData.frequency === "weekly" && formData.is_recurring && (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <FormHelperText sx={{ fontSize: "0.875rem", color: "text.primary" }}>
                    Select days of the week
                  </FormHelperText>
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    {weekdays.map((day) => (
                      <Chip
                        key={day.value}
                        label={day.label}
                        onClick={() => toggleWeekday(day.key)}
                        color={(formData.recurring_day || []).includes(day.key.toString()) ? "primary" : "default"}
                        variant={(formData.recurring_day || []).includes(day.key.toString()) ? "filled" : "outlined"}
                        clickable
                        disabled={activityDisabledConfig.recurring_day}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    happens on every
                    {(formData.recurring_day || []).length > 0
                      ? ` ${(formData.recurring_day || []).join(", ")}`
                      : " ...no days selected"}
                  </Typography>
                </Box>
              )}

              {formData.frequency === "monthly" && formData.is_recurring && (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <FieldControl
                    keyName={"monthly_pattern"}
                    label={"Monthly Pattern"}
                    variant={"radio"}
                    onChange={(_key: string, value: any) => {
                      setFormData({ ...formData, monthly_pattern: value });
                    }}
                    value={formData.monthly_pattern}
                    options={getFilterOptions("MONTHLY_RECURRENCE_PATTERN").map((opt: any) => ({
                      value: opt.value,
                      label: opt.display_text,
                    }))}
                    disabled={activityDisabledConfig.monthly_pattern}
                  />
                  {formData.plannedStartDate && (
                    <FormHelperText sx={{ color: "text.secondary", ml: 1 }}>{getMonthlyDescription()}</FormHelperText>
                  )}
                </Box>
              )}

              <FormControl fullWidth>
                <TextField
                  label="Description"
                  multiline
                  minRows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={activityDisabledConfig.description}
                />
              </FormControl>
            </Box>
          )}

          {currentTab === 1 && (
            <Box px={2} display={"flex"} flexDirection={"column"} gap={2}>
              {
                message && (
                  <Alert severity={message.type} onClose={() => setMessage(null)}>
                    {message.text}
                  </Alert>
                )
              }

              {canAddTodo && (
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                  {!showAddTodo ? (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setShowAddTodo(true)}
                      disabled={isCreating}
                    >
                      Add Todo
                    </Button>
                  ) : (
                    <Box display={"flex"} flexDirection={"column"} gap={2} p={2} border={"1px solid"} borderColor={"divider"} borderRadius={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add New Todo"
                      value={todoDescription}
                      onChange={(e) => setTodoDescription(e.target.value)}
                      disabled={isCreating}
                      onKeyDown={async (e) => {
                        if(e.key === "Enter" && !e.shiftKey && todoDescription.trim() && !isCreating) {
                          await handleAddTodo();
                          setShowAddTodo(false);
                        }}
                      }
                    />
                    <Box display={"flex"} justifyContent={"flex-end"} gap={2}>
                      <Button 
                        onClick={() => {
                          setTodoDescription("");
                          setShowAddTodo(false);
                        }
                        } 
                        disabled={isCreating}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained"
                        startIcon={isCreating ? <CircularProgress size={20} /> : <AddIcon />} 
                        onClick={
                          async () => {
                            await handleAddTodo();
                            setShowAddTodo(false);
                          }
                        } 
                        disabled={isCreating || !todoDescription.trim()}
                      >
                        { isCreating ? "Adding..." : "Add Todo" }
                      </Button>
                    </Box>
                  </Box>
                  )}
                </Box>
              )}
              { todos.length === 0 ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight={120}
                  width="100%"
                >
                  <Alert severity="warning">No todos assigned</Alert>
                </Box>
              ) : (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  { todos.map((todo: any) => (
                    <TodoItem
                      key={todo.id}
                      id={todo.id}
                      description={todo.description}
                      completed={todo.status === 1}
                      disabled={!canCompleteTodo || todo.status === 1}
                      isLoading={updatingTodoId === todo.id}
                      onSave={ async (status) => {
                        setUpdatingTodoId(todo.id);
                        try {
                            await updateTodo({ todoId: todo.id, payload: { status } });
                        } finally {
                            setUpdatingTodoId(null);
                        }
                      }}
                    />
                  )
                )}
                </Box>
              )}
            </Box>
          )}

          {currentTab === 2 && stageGateId && activityId && (
            <Box sx={{ textAlign: "center" }} flex={1} px={2} pb={2}>
              <Comments
                topic={["SCHEDULING_AND_PLANNING"]}
                sub_event={["ACTIVITY_COMMENT_ADDED"]}
                object_id={String(activityId)}
                parent_id={String(stageGateId)}
              />
            </Box>
          )}

          <Dialog open={!!currentWarning} onClose={handleWarningCancel}>
            <DialogTitle>Activity Date Warning</DialogTitle>
            <DialogContent>
              <DialogContentText>{currentWarning}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleWarningCancel}>Cancel</Button>
              <Button onClick={handleWarningIgnore} autoFocus>
                {warningQueue.length > 1 && warningQueue.indexOf(currentWarning!) < warningQueue.length - 1
                  ? "Ignore & Continue"
                  : "Ignore & Save"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    );
  }
);

export const ActivityFormActions: React.FC<{
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  isDeleteAllowed?: boolean;
  isEstimateValidationStageGateCompleted?: boolean;
  isCommercialFocusActivity?: boolean;
}> = ({
  onSave,
  onCancel,
  onDelete,
  isEditing,
  isDeleteAllowed,
  isEstimateValidationStageGateCompleted,
  isCommercialFocusActivity,
}) => (
  <>
    <Button variant="outlined" onClick={onCancel} sx={{ textTransform: "uppercase" }}>
      Cancel
    </Button>
    {isEditing &&
      onDelete &&
      isDeleteAllowed &&
      (!isEstimateValidationStageGateCompleted || !isCommercialFocusActivity) && (
        <Button variant="outlined"  color="primary" onClick={onDelete} sx={{ textTransform: "uppercase" }}>
          Delete
        </Button>
      )}
    <Button variant="contained" onClick={onSave} sx={{ textTransform: "uppercase" }}>
      Save Details
    </Button>
  </>
);